import { useState, useRef, useEffect, useMemo } from "react";
import { format } from "date-fns";
import ChevronIcon from "../../../../../../../assets/svg/chevron-icon";
import TrashIcon from "../../../../../../../assets/svg/trash-icon";
import {
  FilterConditionUI,
  FilterFieldOption,
  DURATION_OPTIONS,
  HELPDESK_FILTER_BETWEEN_POPPER_OPTIONS,
  FILTER_CONDITION_FIELD_PLACEHOLDER,
  FILTER_CONDITION_OPERATOR_PLACEHOLDER,
  FILTER_CONDITION_VALUE_LOADING,
  FILTER_NUMBER_VALUE_PLACEHOLDERS,
  EXISTS_VALUE_OPTIONS,
  getFilterValuePlaceholder,
  getOperatorsForFilterFieldKey,
  VALUE_OPTIONS_MAP,
} from "../utils/constants";
import FlaggedIcon from "../../../../../../../assets/svg/flagged-icon";
import { getPriorityColor } from "../../AllTickets/utils/functions";
import { FILTER_FIELD_OPTIONS } from "../utils/constants";
import {
  parseTagOptions,
  parseStageOptions,
  buildStageColorMap,
  parsePropertyOptions,
  parseUserOptions,
  getValueOptionsForKey,
  formatFilterBetweenValueDisplay,
  getDurationPopperInitialForFilter,
} from "../utils/functions";
import DurationPopper from "../../../../../../../components/DurationPopper/DurationPopper";
import type { DurationOption } from "../../../../../../../components/DurationPopper/utils/constants";
import {
  useGetHelpdeskTagsQuery,
  useGetHelpdeskTicketsQuery,
  useGetHelpdeskResolutionReasonsQuery,
  useGetTicketCategoriesQuery,
} from "../../../../../../../redux/services/helpdeskApi";
import { useGetPropertiesListQuery } from "../../../../../../../redux/services/playgroundApi";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../../redux/store";
import { getClientDomain } from "../../../../../../../utils/functions/client-code";
import { isHelpdeskResolutionClient } from "../../../utils/function";
import { useOrgUsersList } from "../../../../../../../hooks/useOrgUsersList";
import type { HelpdeskCustomFieldDefinition } from "../../TicketDetails/components/TicketOverview/utils/helpdeskCustomFields";
import { renderSystemIcon } from "../../KnowledgeBase/utils/categoryIcons";
import MultiSelectSearchableDropdown from "../../../../../../../components/MultiSelectSearchableDropdown/MultiSelectSearchableDropdown";
import SearchInput from "../../../../../../../components/SearchableDropdown/components/SearchInput";
import { TruncatedTextTooltip } from "../../../../../../../components/TruncatedTextTooltip/TruncatedTextTooltip";
import { convertLocalToUtcIsoString } from "../../../../../../../utils/functions/date-time";

interface FilterConditionRowProps {
  condition: FilterConditionUI;
  onChange: (updated: FilterConditionUI) => void;
  onDelete: () => void;
  showBackground?: boolean;
  fieldOptions?: FilterFieldOption[];
  customFieldDefinitions?: HelpdeskCustomFieldDefinition[];
}

export const FilterDropdown = ({
  options,
  value,
  onChange,
  placeholder,
  renderOption,
  renderSelected,
  width,
  textClassName,
  customTrigger,
  dropdownClassName,
  disabled = false,
  isLoading = false,
  showSearch = false,
  searchPlaceholder = "Search...",
  groupBy,
  renderGroupHeader,
  groupOrder,
}: {
  options: { key: string; label: string }[];
  value?: string;
  onChange: (key: string) => void;
  placeholder?: string;
  renderOption?: (opt: { key: string; label: string }) => React.ReactNode;
  renderSelected?: (opt: { key: string; label: string }) => React.ReactNode;
  width?: string;
  textClassName?: string;
  customTrigger?: (open: boolean) => React.ReactNode;
  dropdownClassName?: string;
  disabled?: boolean;
  isLoading?: boolean;
  showSearch?: boolean;
  searchPlaceholder?: string;
  groupBy?: (opt: { key: string; label: string }) => string | undefined;
  renderGroupHeader?: (group: string) => React.ReactNode;
  groupOrder?: string[];
}) => {
  const [open, setOpen] = useState(false);
  const [openAbove, setOpenAbove] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref?.current && !ref?.current?.contains(e?.target as Node)) {
        setOpen(false);
        setSearchQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open && showSearch) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [open, showSearch]);

  const handleToggle = () => {
    if (disabled || isLoading) return;
    if (!open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const dropdownHeight = Math.min(options.length * 32, 300);
      const scrollParent = ref.current.closest(".overflow-y-auto");
      const bottomEdge = scrollParent
        ? scrollParent.getBoundingClientRect().bottom
        : window.innerHeight;
      const spaceBelow = bottomEdge - rect.bottom - 4;
      setOpenAbove(spaceBelow < dropdownHeight);
    }
    setOpen((p) => {
      if (p) setSearchQuery("");
      return !p;
    });
  };

  const selected = options?.find((o) => o?.key === value);

  const filteredOptions = useMemo(() => {
    if (!showSearch || !searchQuery.trim()) return options;
    const query = searchQuery.toLowerCase().trim();
    return options.filter((opt) => opt.label.toLowerCase().includes(query));
  }, [options, searchQuery, showSearch]);

  const groupedOptions = useMemo(() => {
    if (!groupBy) return null;
    const buckets: Record<string, typeof filteredOptions> = {};
    const orderSeen: string[] = [];
    filteredOptions.forEach((opt) => {
      const g = groupBy(opt) ?? "";
      if (!buckets[g]) {
        buckets[g] = [];
        orderSeen.push(g);
      }
      buckets[g].push(opt);
    });
    const order = groupOrder
      ? [
          ...groupOrder.filter((g) => buckets[g]?.length),
          ...orderSeen.filter((g) => !groupOrder.includes(g)),
        ]
      : orderSeen;
    return order.map((g) => ({ group: g, items: buckets[g] }));
  }, [filteredOptions, groupBy, groupOrder]);

  return (
    <div ref={ref} className={`relative ${width || "w-[150px]"}`}>
      {customTrigger ? (
        <div onClick={handleToggle} className="cursor-pointer">
          {customTrigger(open)}
        </div>
      ) : (
        <button
          type="button"
          onClick={handleToggle}
          disabled={disabled || isLoading}
          className={`bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg h-[32px] w-full flex items-center justify-between px-2 gap-1 transition-colors focus:outline-none ${disabled || isLoading ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
        >
          <span
            className={`text-md text-verba-indigo dark:text-verba-indigo-dk truncate flex-1 text-left flex items-center gap-1.5 ${textClassName || ""}`}
          >
            {isLoading ? (
              <span className="text-gray-placeholder dark:text-gray-placeholder-dk truncate">
                {FILTER_CONDITION_VALUE_LOADING}
              </span>
            ) : selected ? (
              renderSelected ? (
                renderSelected(selected)
              ) : (
                selected?.label
              )
            ) : (
              <span className="text-gray-placeholder dark:text-gray-placeholder-dk">
                {placeholder}
              </span>
            )}
          </span>
          {!isLoading && (
            <ChevronIcon
              className={`w-[14px] h-[14px] shrink-0 text-verba-indigo dark:text-verba-indigo-dk transition-transform ${open ? "rotate-180" : ""}`}
            />
          )}
        </button>
      )}
      {open && (
        <div
          className={`absolute left-0 z-50 w-full min-w-[140px] max-w-[220px] bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg shadow-popper-blur-effect max-h-[300px] overflow-x-hidden flex flex-col ${
            openAbove ? "bottom-[36px]" : "top-[34px]"
          } ${dropdownClassName || ""}`}
        >
          {showSearch && (
            <SearchInput
              searchQuery={searchQuery}
              searchPlaceholder={searchPlaceholder}
              searchInputRef={searchInputRef}
              onSearchQueryChange={setSearchQuery}
              showSearch
            />
          )}
          <div className="overflow-y-auto flex-1">
            {filteredOptions.length === 0 ? (
              <div className="p-3 text-md text-gray-placeholder dark:text-gray-placeholder-dk text-center">
                No results found
              </div>
            ) : groupedOptions ? (
              groupedOptions.map(({ group, items }) => (
                <div key={group || "__none"}>
                  {renderGroupHeader && group ? renderGroupHeader(group) : null}
                  {items.map((opt) => (
                    <button
                      key={opt?.key}
                      type="button"
                      onClick={() => {
                        onChange(opt?.key);
                        setOpen(false);
                        setSearchQuery("");
                      }}
                      className={`w-full flex items-center gap-1.5 p-3 text-md hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors cursor-pointer min-w-0 ${
                        opt?.key === value
                          ? "bg-card-highlight dark:bg-card-highlight-dk text-verba-indigo dark:text-verba-indigo-dk font-medium"
                          : "text-verba-indigo dark:text-verba-indigo-dk"
                      }`}
                    >
                      {renderOption ? (
                        <span className="flex items-center gap-1.5 min-w-0 overflow-hidden">
                          {renderOption(opt)}
                        </span>
                      ) : (
                        <TruncatedTextTooltip
                          text={opt?.label}
                          side="right"
                          maxWidth="max-w-full"
                        />
                      )}
                    </button>
                  ))}
                </div>
              ))
            ) : (
              filteredOptions.map((opt, index) => (
                <button
                  key={opt?.key}
                  type="button"
                  onClick={() => {
                    onChange(opt?.key);
                    setOpen(false);
                    setSearchQuery("");
                  }}
                  className={`w-full flex items-center gap-1.5 p-3 text-md hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors cursor-pointer border-border-gray dark:border-border-gray-dk min-w-0 ${
                    filteredOptions.length - 1 === index
                      ? "border-b-0"
                      : "border-b"
                  } ${
                    opt?.key === value
                      ? "bg-card-highlight dark:bg-card-highlight-dk text-verba-indigo dark:text-verba-indigo-dk font-medium"
                      : "text-verba-indigo dark:text-verba-indigo-dk"
                  }`}
                >
                  {renderOption ? (
                    <span className="flex items-center gap-1.5 min-w-0 overflow-hidden">
                      {renderOption(opt)}
                    </span>
                  ) : (
                    <TruncatedTextTooltip
                      text={opt?.label}
                      side="right"
                      maxWidth="max-w-full"
                    />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const PriorityBadge = ({ valueKey }: { valueKey: string }) => {
  const options = VALUE_OPTIONS_MAP?.["priority"] || [];
  const opt = options?.find((o) => o?.key === valueKey);
  if (!opt) return null;

  const colorClass = getPriorityColor(opt?.label);
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${colorClass}`}>
      <FlaggedIcon className={`w-3.5 h-3.5 shrink-0 ${colorClass}`} />
      {opt?.label}
    </span>
  );
};

const StageBadge = ({
  stageKey,
  label,
  hexColor,
}: {
  stageKey: string;
  label: string;
  hexColor?: string;
}) => {
  return (
    <span
      className="inline-flex items-center justify-center px-2 py-[2px] rounded-[6px] text-(length:--font-size-sm) font-semibold uppercase text-white leading-[1.4] text-start"
      style={{ backgroundColor: hexColor || "#6b7280" }}
    >
      {label || stageKey}
    </span>
  );
};

export const FilterConditionRow = ({
  condition,
  onChange,
  onDelete,
  showBackground = true,
  fieldOptions: fieldOptionsProp,
  customFieldDefinitions,
}: FilterConditionRowProps) => {
  const authCurrentClientCode = useSelector(
    (state: RootState) => state?.auth?.user?.current_client_code
  );
  const [betweenDurationOpen, setBetweenDurationOpen] = useState(false);
  const isResolutionEnabled = isHelpdeskResolutionClient(getClientDomain());
  const [autoSelectedKey, setAutoSelectedKey] = useState<string | null>(null);
  const fieldOptions = fieldOptionsProp ?? FILTER_FIELD_OPTIONS;
  const selectedField = fieldOptions?.find((f) => f?.key === condition?.key);

  const needsUserDirectory =
    condition?.key === "assignees" || condition?.key === "created_by";

  const { activeUsers, isLoading: isUsersLoading } = useOrgUsersList({
    skip: !needsUserDirectory,
  });

  const { data: helpdeskTagsData, isLoading: isTagsLoading } =
    useGetHelpdeskTagsQuery({}, { skip: condition?.key !== "tags" });

  const { data: helpdeskBoardData, isLoading: isBoardLoading } =
    useGetHelpdeskTicketsQuery({});

  const { data: propertiesData, isLoading: isPropertiesLoading } =
    useGetPropertiesListQuery(
      { managerId: authCurrentClientCode ?? "", page: 1, pageSize: 1000 },
      { skip: condition?.key !== "property_name" || !authCurrentClientCode }
    );

  const { data: resolutionReasonsData, isLoading: isResolutionsLoading } =
    useGetHelpdeskResolutionReasonsQuery(
      {},
      { skip: !isResolutionEnabled || condition?.key !== "resolution_reason" }
    );

  const userDirectoryOptions = useMemo(
    () => parseUserOptions(activeUsers),
    [activeUsers]
  );

  const agentOptions = userDirectoryOptions;
  const userOptions = userDirectoryOptions;

  const tagOptions = useMemo(
    () => parseTagOptions(helpdeskTagsData),
    [helpdeskTagsData]
  );

  const stageOptions = useMemo(
    () => parseStageOptions(helpdeskBoardData?.stages),
    [helpdeskBoardData]
  );

  const stageColorMap = useMemo(
    () => buildStageColorMap(helpdeskBoardData?.stages),
    [helpdeskBoardData]
  );

  const propertyOptions = useMemo(
    () => parsePropertyOptions(propertiesData?.res),
    [propertiesData]
  );

  const resolutionOptions = useMemo(() => {
    const raw = resolutionReasonsData as
      | { reasons?: string[] }
      | string[]
      | undefined;
    const reasons = Array.isArray(raw) ? raw : (raw?.reasons ?? []);
    return reasons.map((reason: string) => ({ key: reason, label: reason }));
  }, [resolutionReasonsData]);

  const { data: ticketCategoriesData, isLoading: isCategoriesLoading } =
    useGetTicketCategoriesQuery(undefined, {
      skip: condition?.key !== "ticket_category",
    });

  const ticketCategoryOptions = useMemo(() => {
    if (!ticketCategoriesData) return [];
    return ticketCategoriesData.map((c) => ({
      key: c.key,
      label: c.display_name,
    }));
  }, [ticketCategoriesData]);

  const valueOptions = useMemo(
    () =>
      getValueOptionsForKey(
        condition?.key,
        agentOptions,
        tagOptions,
        stageOptions,
        propertyOptions,
        userOptions,
        customFieldDefinitions,
        resolutionOptions,
        ticketCategoryOptions
      ),
    [
      condition?.key,
      agentOptions,
      tagOptions,
      stageOptions,
      propertyOptions,
      userOptions,
      customFieldDefinitions,
      resolutionOptions,
      ticketCategoryOptions,
    ]
  );

  const getCustomFieldType = (key: string) =>
    customFieldDefinitions?.find((f) => f?.key === key)?.type;

  const currentFieldType = getCustomFieldType(condition?.key);

  const operatorOptions = useMemo(
    () => getOperatorsForFilterFieldKey(condition?.key ?? "", currentFieldType),
    [condition?.key, currentFieldType]
  );

  const isValueLoading = useMemo(() => {
    if (!condition?.key) return false;
    if (needsUserDirectory && isUsersLoading) return true;
    if (condition?.key === "tags" && isTagsLoading) return true;
    if (condition?.key === "stage_key" && isBoardLoading) return true;
    if (condition?.key === "property_name" && isPropertiesLoading) return true;
    if (condition?.key === "resolution_reason" && isResolutionsLoading)
      return true;
    if (condition?.key === "ticket_category" && isCategoriesLoading)
      return true;
    return false;
  }, [
    condition?.key,
    needsUserDirectory,
    isUsersLoading,
    isTagsLoading,
    isBoardLoading,
    isPropertiesLoading,
    isResolutionsLoading,
    isCategoriesLoading,
  ]);

  const valuePlaceholder = useMemo(
    () => getFilterValuePlaceholder(condition?.key ?? "", currentFieldType),
    [condition?.key, currentFieldType]
  );

  const durationPopperInitial = useMemo(() => {
    const summary = formatFilterBetweenValueDisplay(condition?.value ?? []);
    if (!summary) {
      return {
        initialOption: { id: "custom", label: "Custom" } as DurationOption,
      };
    }
    return getDurationPopperInitialForFilter(condition?.value ?? []);
  }, [condition?.value]);

  const hasBetweenDurationFilterValue = useMemo(
    () => Boolean(formatFilterBetweenValueDisplay(condition?.value ?? [])),
    [condition?.value]
  );

  const handleFieldChange = (key: string) => {
    const isCustom = !FILTER_FIELD_OPTIONS?.some((f) => f?.key === key);
    const fieldType = getCustomFieldType(key);
    const defaultOp = getOperatorsForFilterFieldKey(key, fieldType)[0]?.key;
    onChange({
      ...condition,
      key,
      value: [],
      comparison_operator: defaultOp,
      is_custom_field: isCustom,
    });
  };

  const isDurationField = condition?.key === "duration";

  const handleOperatorChange = (op: string) => {
    const wasExists = condition?.comparison_operator === "exists";
    const isExists = op === "exists";
    onChange({
      ...condition,
      comparison_operator: op,
      ...(wasExists || isExists ? { value: [] } : {}),
    });
  };

  const isExistsOperator = condition?.comparison_operator === "exists";
  const isNumberField = currentFieldType === "number";
  const isTextField =
    currentFieldType === "text" || currentFieldType === "long_text";
  const isDateField = currentFieldType === "date";
  const isBetweenDateOrDuration =
    (isDateField || isDurationField) &&
    condition?.comparison_operator === "range";

  useEffect(() => {
    if (!isBetweenDateOrDuration) setBetweenDurationOpen(false);
  }, [isBetweenDateOrDuration]);

  const isSingleValueField =
    [
      "created_by",
      "stage_key",
      "request_support",
      "resolution_reason",
    ].includes(condition?.key) ||
    currentFieldType === "dropdown_single_select" ||
    currentFieldType === "radio_single_select";

  const isStandardValueDropdown =
    !isExistsOperator &&
    !isBetweenDateOrDuration &&
    !isNumberField &&
    !isTextField;

  useEffect(() => {
    if (!condition?.key) return;
    if (isValueLoading) return;
    if (!isStandardValueDropdown) return;
    if (autoSelectedKey === condition?.key) return;
    if ((condition?.value?.length ?? 0) > 0) {
      setAutoSelectedKey(condition?.key);
      return;
    }
    if (valueOptions?.length !== 1) return;
    setAutoSelectedKey(condition?.key);
    onChange({ ...condition, value: [valueOptions[0]?.key] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    condition?.key,
    valueOptions,
    isValueLoading,
    isStandardValueDropdown,
    condition?.value?.length,
    autoSelectedKey,
  ]);

  const handleValueChange = (vals: string[]) => {
    onChange({ ...condition, value: vals });
  };

  const handleSingleValueChange = (val: string) => {
    onChange({ ...condition, value: [val] });
  };

  const handleNumberInputChange = (val: string, index: number = 0) => {
    const newValue = [...(condition?.value || [])];
    newValue[index] = val;
    onChange({ ...condition, value: newValue });
  };

  const handleTextInputChange = (val: string) => {
    onChange({ ...condition, value: [val] });
  };

  const filterRow = (
    <>
      <FilterDropdown
        options={fieldOptions?.map((f) => ({ key: f?.key, label: f?.label }))}
        value={condition?.key}
        onChange={handleFieldChange}
        placeholder={FILTER_CONDITION_FIELD_PLACEHOLDER}
        width="min-w-[150px] max-w-[150px]"
        dropdownClassName="!min-w-[200px]"
        renderOption={(opt) => {
          const field = fieldOptions?.find((f) => f?.key === opt?.key);
          if (!field) return opt?.label;
          const Icon = field?.icon;
          return (
            <>
              <Icon
                className={`shrink-0 ${field.iconClassName || "w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk"}`}
              />
              <span className="truncate">{opt?.label}</span>
            </>
          );
        }}
        renderSelected={(opt) => {
          if (!selectedField) return opt?.label;
          const Icon = selectedField?.icon;
          return (
            <>
              <Icon
                className={`shrink-0 ${selectedField.iconClassName ? selectedField.iconClassName.replace(/w-\[18px\]/g, "w-[14px]").replace(/h-\[18px\]/g, "h-[14px]") : "w-[14px] h-[14px] text-verba-indigo dark:text-verba-indigo-dk"}`}
              />
              <span className="truncate">{opt?.label}</span>
            </>
          );
        }}
      />

      {condition?.key &&
        (operatorOptions?.length === 1 ? (
          <span className="bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg h-[32px] max-w-[90px] flex items-center justify-center px-2 text-md font-medium text-verba-indigo dark:text-verba-indigo-dk select-none whitespace-nowrap">
            {operatorOptions?.[0]?.label}
          </span>
        ) : (
          <FilterDropdown
            options={operatorOptions}
            value={condition?.comparison_operator}
            onChange={handleOperatorChange}
            placeholder={FILTER_CONDITION_OPERATOR_PLACEHOLDER}
            width="w-[90px]"
            textClassName="font-medium"
          />
        ))}

      {condition?.key &&
        (isExistsOperator ? (
          <FilterDropdown
            options={EXISTS_VALUE_OPTIONS}
            value={condition?.value?.[0] || ""}
            onChange={handleSingleValueChange}
            placeholder="Select"
            width="min-w-[100px] max-w-[100px]"
          />
        ) : isBetweenDateOrDuration ? (
          <DurationPopper
            key={`${condition.key}-${condition.value?.join("|") ?? ""}-between`}
            variant="default"
            isOpen={betweenDurationOpen}
            onOpen={() => setBetweenDurationOpen(true)}
            onClose={() => setBetweenDurationOpen(false)}
            applyOnMount={false}
            durationOptions={
              HELPDESK_FILTER_BETWEEN_POPPER_OPTIONS as DurationOption[]
            }
            title="Duration"
            triggerName={valuePlaceholder}
            triggerAriaLabel={valuePlaceholder}
            initialDates={durationPopperInitial.initialDates}
            initialOption={durationPopperInitial.initialOption}
            onApply={(from, to, selectedDurationOption) => {
              const optionId = selectedDurationOption?.id ?? "custom";
              onChange({
                ...condition,
                value: [
                  convertLocalToUtcIsoString(
                    format(from, "yyyy-MM-dd'T'HH:mm:ss")
                  ),
                  convertLocalToUtcIsoString(
                    format(to, "yyyy-MM-dd'T'HH:mm:ss")
                  ),
                  optionId,
                ],
              });
            }}
            portalHorizontalAlign="end"
            className="flex-1 min-w-0 self-center"
            triggerClassName="!rounded-lg !h-[32px] !max-h-[32px] !min-h-[32px] w-full !bg-pure-white dark:!bg-pure-white-dk !backdrop-blur-none border border-solid border-border-gray dark:border-border-gray-dk !shadow-none px-0"
            triggerIconClassName="!w-[14px] !h-[14px] !shrink-0 !text-verba-indigo dark:!text-verba-indigo-dk !transition-transform"
            selectedOptionContainerClassName="!h-full !min-h-0 !max-w-[150px] min-w-0 !justify-between !px-2 !py-0 items-center"
            triggerTextClassName={
              hasBetweenDurationFilterValue
                ? "text-md font-medium !text-verba-indigo dark:!text-verba-indigo-dk"
                : "text-md font-medium !text-gray-placeholder dark:!text-gray-placeholder-dk"
            }
          />
        ) : isDurationField ? (
          <FilterDropdown
            options={DURATION_OPTIONS}
            value={condition?.value?.[0] || ""}
            onChange={handleSingleValueChange}
            placeholder={valuePlaceholder}
            width="min-w-[150px] max-w-[150px]"
            isLoading={isValueLoading}
          />
        ) : isNumberField ? (
          <div className="flex items-center gap-1 flex-1 min-w-0">
            <input
              type="number"
              value={condition?.value?.[0] || ""}
              onChange={(e) => handleNumberInputChange(e.target.value, 0)}
              onKeyDown={(e) => {
                if (["e", "E", "+"].includes(e.key)) e.preventDefault();
              }}
              placeholder={
                condition?.comparison_operator === "range"
                  ? FILTER_NUMBER_VALUE_PLACEHOLDERS.min
                  : FILTER_NUMBER_VALUE_PLACEHOLDERS.single
              }
              className="bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg h-[32px] w-full px-2 text-md text-verba-indigo dark:text-verba-indigo-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk outline-none transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            {condition?.comparison_operator === "range" && (
              <>
                <span className="text-xs text-gray-placeholder dark:text-gray-placeholder-dk shrink-0">
                  –
                </span>
                <input
                  type="number"
                  value={condition?.value?.[1] || ""}
                  onChange={(e) => handleNumberInputChange(e.target.value, 1)}
                  onKeyDown={(e) => {
                    if (["e", "E", "+"].includes(e.key)) e.preventDefault();
                  }}
                  placeholder={FILTER_NUMBER_VALUE_PLACEHOLDERS.max}
                  className="bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg h-[32px] w-full px-2 text-md text-verba-indigo dark:text-verba-indigo-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk outline-none transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </>
            )}
          </div>
        ) : isTextField ? (
          <div className="flex-1 min-w-0">
            <input
              type="text"
              value={condition?.value?.[0] || ""}
              onChange={(e) => handleTextInputChange(e.target.value)}
              placeholder={valuePlaceholder}
              className="bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg h-[32px] w-full px-2 text-md text-verba-indigo dark:text-verba-indigo-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk outline-none transition-colors"
            />
          </div>
        ) : isDateField ? (
          <FilterDropdown
            options={DURATION_OPTIONS}
            value={condition?.value?.[0] || ""}
            onChange={handleSingleValueChange}
            placeholder={valuePlaceholder}
            width="min-w-[150px] max-w-[150px]"
            isLoading={isValueLoading}
          />
        ) : isSingleValueField ? (
          <FilterDropdown
            options={valueOptions}
            value={condition?.value?.[0] || ""}
            onChange={handleSingleValueChange}
            placeholder={valuePlaceholder}
            isLoading={isValueLoading}
            showSearch={condition?.key === "system-name"}
            searchPlaceholder="Search system..."
            width={
              condition?.key === "stage_key"
                ? "min-w-[150px] max-w-[180px]"
                : "min-w-[150px] max-w-[150px]"
            }
            dropdownClassName={
              condition?.key === "stage_key"
                ? "!max-w-[240px]"
                : condition?.key === "system-name"
                  ? "!min-w-[170px]"
                  : undefined
            }
            renderOption={
              condition?.key === "stage_key"
                ? (opt) => (
                    <StageBadge
                      stageKey={opt?.key}
                      label={opt?.label}
                      hexColor={stageColorMap?.[opt?.key]}
                    />
                  )
                : condition?.key === "system-name"
                  ? (opt) => (
                      <>
                        {renderSystemIcon(opt?.label)}
                        <TruncatedTextTooltip text={opt?.label} />
                      </>
                    )
                  : undefined
            }
            renderSelected={
              condition?.key === "stage_key"
                ? (opt) => (
                    <StageBadge
                      stageKey={opt?.key}
                      label={opt?.label}
                      hexColor={stageColorMap?.[opt?.key]}
                    />
                  )
                : condition?.key === "system-name"
                  ? (opt) => (
                      <>
                        {renderSystemIcon(opt?.label)}
                        <TruncatedTextTooltip text={opt?.label} />
                      </>
                    )
                  : undefined
            }
          />
        ) : (
          <div className="min-w-[150px] max-w-[150px]">
            <MultiSelectSearchableDropdown
              disabled={isValueLoading}
              options={
                isValueLoading
                  ? []
                  : valueOptions?.map((opt) => ({
                      value: opt?.key,
                      label: opt?.label,
                      selectedOptionContent: opt?.label,
                      content: (
                        <span className="flex items-center gap-1.5 text-md min-w-0 text-verba-indigo dark:text-verba-indigo-dk">
                          {condition?.key === "stage_key" ? (
                            <StageBadge
                              stageKey={opt?.key}
                              label={opt?.label}
                              hexColor={stageColorMap?.[opt?.key]}
                            />
                          ) : condition?.key === "priority" ? (
                            <PriorityBadge valueKey={opt?.key} />
                          ) : condition?.key === "system-name" ? (
                            <>
                              {renderSystemIcon(opt?.label)}
                              <TruncatedTextTooltip text={opt?.label} />
                            </>
                          ) : (
                            <TruncatedTextTooltip text={opt?.label} />
                          )}
                        </span>
                      ),
                    }))
              }
              value={condition?.value || []}
              onChange={(vals) => handleValueChange(vals)}
              placeholder={
                isValueLoading
                  ? FILTER_CONDITION_VALUE_LOADING
                  : valuePlaceholder
              }
              showSearch={!isValueLoading && valueOptions?.length > 4}
              showSelectedOptionsChips={true}
              autoFitChips
              moreTagText=""
              chipClassName="inline-flex items-center px-0 py-0 text-md font-medium rounded-none border-none bg-transparent text-heading-text dark:text-heading-text-dk shrink min-w-0 [&>button]:hidden after:content-[','] last:after:content-[''] after:ml-[1.5px]"
              moreTagClassName="inline-flex items-center px-1.5 py-0.5 text-xs rounded-md bg-tag-gray dark:bg-tag-gray-dk text-heading-text dark:text-heading-text-dk shrink-0 font-medium"
              triggerClassName="!rounded-lg h-[32px]"
              inputContainerClassName="!py-0 h-full rounded-lg !bg-pure-white dark:!bg-pure-white-dk"
              placeholderClassName="text-md font-medium text-gray-placeholder dark:text-gray-placeholder-dk"
              selectedOptionClassName="!bg-card-highlight-2 dark:!bg-card-highlight-2-dk !border-b-selected-option-highlight dark:!border-b-selected-option-highlight-dk"
              dropdownClassName="!max-h-[200px] !min-w-[170px]"
              optionsListClassName="!py-0"
              className="w-full"
            />
          </div>
        ))}

      <button
        type="button"
        onClick={onDelete}
        className="shrink-0 w-[14px] h-[14px] flex items-center justify-center cursor-pointer text-dark-gray dark:text-dark-gray-dk hover:text-verba-red-alert dark:hover:text-verba-red-alert-dk transition-colors"
      >
        <TrashIcon className="w-[14px] h-[14px]" />
      </button>
    </>
  );

  return (
    <div
      className={`flex items-center gap-2 h-[48px] ${showBackground ? "bg-hover-effect dark:bg-hover-effect-dk rounded-lg p-2" : ""}`}
    >
      {filterRow}
    </div>
  );
};
